import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { useSiteMetadata } from '../hooks/useSiteMetadata';

// Google Consent Mode v2 - Initialize with immediate consent checking
const consentModeScript = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}

// Check for existing consent BEFORE setting defaults
(function() {
  var existingConsent = null;
  try {
    var stored = localStorage.getItem('cookie-consent');
    var sessionStored = sessionStorage.getItem('cookie-consent-session');
    
    if (stored) {
      existingConsent = JSON.parse(stored);
    } else if (sessionStored) {
      existingConsent = JSON.parse(sessionStored);
      // If we found it in session but not localStorage, restore to localStorage
      localStorage.setItem('cookie-consent', sessionStored);
    }
  } catch (e) {
    console.warn('Failed to load stored consent:', e);
  }

  // If we have existing consent, apply it immediately
  if (existingConsent) {
    gtag('consent', 'default', {
      'analytics_storage': existingConsent.analytics ? 'granted' : 'denied',
      'ad_storage': existingConsent.marketing ? 'granted' : 'denied',
      'ad_user_data': existingConsent.marketing ? 'granted' : 'denied',
      'ad_personalization': existingConsent.marketing ? 'granted' : 'denied',
      'functionality_storage': existingConsent.functionality ? 'granted' : 'denied',
      'security_storage': 'granted',
      'wait_for_update': 2000
    });
  } else {
    // No existing consent - set ALL to denied until user consents (with EEA region config)
    gtag('consent', 'default', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'functionality_storage': 'denied',
      'security_storage': 'granted',
      'wait_for_update': 2000,
      'region': ['BE', 'BG', 'CZ', 'DK', 'DE', 'EE', 'IE', 'EL', 'ES', 'FR', 'HR', 'IT', 'CY', 'LV', 'LT', 'LU', 'HU', 'MT', 'NL', 'AT', 'PL', 'PT', 'RO', 'SI', 'SK', 'FI', 'SE', 'IS', 'LI', 'NO']
    });
  }
})();
`;

const googleTagScript = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-MHDB9JXG');`;

function Seo({ description, lang, meta, title }) {
  const site = useSiteMetadata();

  const metaDescription = description || site.description;

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.title}`}
      meta={[
        {
          name: 'description',
          content: metaDescription,
        },
        {
          property: 'og:title',
          content: `${title} | ${site.title}`,
        },
        {
          property: 'og:description',
          content: metaDescription,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          name: 'twitter:card',
          content: 'summary',
        },
        {
          name: 'twitter:creator',
          content: site.author,
        },
        {
          name: 'twitter:title',
          content: `${title} | ${site.title}`,
        },
        {
          name: 'twitter:description',
          content: metaDescription,
        },
      ].concat(meta)}
      script={[
        {
          type: 'text/javascript',
          innerHTML: consentModeScript
        },
        {
          type: 'text/javascript',
          innerHTML: googleTagScript
        },
      ]}
    />
  );
}

Seo.defaultProps = {
  lang: 'it',
  meta: [],
  description: '',
};

Seo.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
};

export default Seo;
